import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  InputAdornment,
  MenuItem,
  Radio,
  Select,
  Skeleton,
  TextField,
  Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import SearchIcon from '@mui/icons-material/Search';
import CampaignOutlinedIcon from '@mui/icons-material/CampaignOutlined';
import {
  appModalActionsSx,
  appModalCloseButtonSx,
  appModalHeaderDividerSx,
  appModalPaperSx,
  appModalPrimaryButtonSx,
  appModalPrimaryLoadingSx,
  appModalSecondaryButtonSx,
  appModalTitleBarSx,
  appModalTitleSx,
} from '../../Components/appModalChrome.styles';
import { LEADS_TEXT_SECONDARY, leadsAlertSx, leadsSelectMenuProps } from '../../Leads/components/leadsUi.styles';
import { listSearchFieldSx, listSearchIconSx } from '../../Map/components/UserLotListsPanel/UserLotListsPanel.styles';
import { colors } from '../../styles/theme';
import { getScrollEdgeFadeSx, useScrollEdgeFade } from '../../hooks/useScrollEdgeFade';
import { linkCampaignUnit } from '../../services/metaAdsService';
import { getMetaErrorMessage, readMetaErrorCode } from '../metaAdsConfig';
import { useMetaCampaigns } from '../hooks/useMetaCampaigns';
import { unitCampaignsPeriod } from '../metaUnitCampaigns';
import {
  describeMetaOtherUnits, metaCampaignThumb, metaUnitLinkDetail, metaUnitLinkMetrics, metaUnitLinkRows,
} from '../metaUnitLink';
import MetaAdThumb from './MetaAdThumb';
import {
  getMetaAdsStatusPillSx, metaAdsSkeletonSx, metaLinkFilterSelectSx,
} from './metaAdsUi.styles';

export const META_LINK_CAMPAIGN_HINT = 'Se vinculan los anuncios que la campaña tiene hoy.';
// 409 de respaldo (las campañas sin anuncios ya se filtran): mensaje corto y se recarga la lista.
export const META_LINK_CAMPAIGN_NO_ADS = 'Esta campaña no tiene anuncios para vincular. Actualizamos la lista.';
const RELOAD_CODES = ['META_CAMPAIGN_NO_ADS', 'META_CAMPAIGN_NOT_FOUND'];

const withCount = (label, n) => (n == null ? label : `${label} (${n})`);
// Con campañas el alto es fijo: la lista scrollea adentro.
// Al cargar, vacío, sin coincidencias o con error, el diálogo abraza el contenido.
const DIALOG_HEIGHT = 'min(90vh, 620px)';

/*
 * Valor del desplegable «Activas (N) / Todas (M)»: las dos etiquetas superpuestas en la misma celda
 * (patrón de `MetaPeriodSelect`), solo la elegida visible → el botón no cambia de ancho al elegir.
 */
const filterSizerSx = { display: 'inline-grid', verticalAlign: 'top' };
const filterLabelSx = { gridArea: '1 / 1', whiteSpace: 'nowrap', textAlign: 'left' };
function FilterValue({ value, options }) {
  return (
    <Box component="span" sx={filterSizerSx}>
      {options.map((o) => (
        <Box key={o.value} component="span" sx={{ ...filterLabelSx, visibility: o.value === value ? 'visible' : 'hidden' }} aria-hidden={o.value === value ? undefined : true} data-testid={o.value === value ? 'meta-link-campaign-filter-value' : undefined}>
          {o.label}
        </Box>
      ))}
    </Box>
  );
}
const rowSx = (selected, disabled) => ({
  display: 'grid',
  gridTemplateColumns: '28px 40px minmax(0, 1fr)',
  columnGap: 1.25,
  alignItems: 'center',
  px: 0.75,
  py: 1,
  borderRadius: '8px',
  cursor: disabled ? 'default' : 'pointer',
  bgcolor: selected ? 'rgba(72,146,145,0.12)' : 'transparent',
  '&:hover': disabled ? {} : { bgcolor: selected ? 'rgba(72,146,145,0.16)' : 'rgba(255,255,255,0.04)' },
  '& + &': { borderTop: '1px solid rgba(255,255,255,0.06)' },
  outline: 'none',
  '&:focus-visible': { boxShadow: `0 0 0 2px ${colors.primaryLight}` },
});
const nameSx = (disabled) => ({
  fontSize: '14px', lineHeight: '20px', fontWeight: 600, overflowWrap: 'anywhere',
  color: disabled ? colors.disabledOnDarkText : colors.textPrimary,
});
const lineSx = (disabled) => ({
  fontSize: '12.5px', lineHeight: '17px', color: disabled ? colors.disabledOnDarkText : 'rgba(255,255,255,0.55)',
});
const radioSx = {
  p: 0.5,
  color: 'rgba(255,255,255,0.55)',
  '&.Mui-checked': { color: colors.primary },
  '&.Mui-disabled': { color: colors.disabledOnDarkText },
  // Ya vinculada (29/10): tildada y deshabilitada, en el color primario (no se puede tocar).
  '&.Mui-checked.Mui-disabled': { color: colors.primary },
};
const linkedLabelSx = { ...lineSx(true), color: colors.primaryLight, fontWeight: 600 };
const chipSx = { ...getMetaAdsStatusPillSx('pending'), ml: 0.75, verticalAlign: 'middle' };

/** Una fila de carga con la misma grilla que una campaña: radio, miniatura y dos líneas. */
function CampaignRowSkeleton() {
  return (
    <Box data-testid="meta-link-campaign-skel-row" aria-hidden sx={{ display: 'grid', gridTemplateColumns: '28px 40px minmax(0, 1fr)', columnGap: 1.25, alignItems: 'center', px: 0.75, py: 1 }}>
      <Skeleton data-testid="meta-link-campaign-skel-radio" variant="circular" width={18} height={18} sx={{ ...metaAdsSkeletonSx, justifySelf: 'center' }} />
      <Skeleton data-testid="meta-link-campaign-skel-bar" variant="rounded" width={40} height={40} sx={{ ...metaAdsSkeletonSx, borderRadius: '6px' }} />
      <Box sx={{ minWidth: 0 }}>
        <Skeleton data-testid="meta-link-campaign-skel-bar" variant="rounded" height={14} width="78%" sx={metaAdsSkeletonSx} />
        <Skeleton data-testid="meta-link-campaign-skel-bar" variant="rounded" height={12} width="52%" sx={{ ...metaAdsSkeletonSx, mt: 0.75 }} />
      </Box>
    </Box>
  );
}

/**
 * «Vincular campaña» desde la ficha (unidad, lote o parcela): elegir UNA campaña de las cuentas
 * seguidas y vincularla entera con este registro (`POST /linkcampaignunit`, `records` = [registro]).
 * La lista (`GET /getmetacampaigns`, mismo período que la ficha) se pide recién al abrir.
 * Mismo chrome, botones y manejo de errores que `MetaLinkUnitsDialog`: si falla queda abierto
 * con un solo aviso. Ya vinculada: gris y deshabilitada; con otras unidades: «También en …».
 *
 * @param {{ open: boolean, record: { itemKind: string, id: string, label?: string|null },
 *   recordLabel?: string, ownerUserId?: string|null, onClose: () => void,
 *   onLinked: (campaign: object, res: object) => void, onOpenCampaigns?: (() => void)|null }} props
 */
export default function MetaLinkCampaignDialog({
  open, record, recordLabel = '', ownerUserId = null, onClose, onLinked, onOpenCampaigns = null,
}) {
  const [includeInactive, setIncludeInactive] = useState(false);
  const period = useMemo(() => unitCampaignsPeriod(), []);
  const campaignsState = useMetaCampaigns({ ownerUserId, enabled: open, includeInactive, period });
  const {
    loaded, loading, error, campaigns, reload, activeCampaignsCount, totalCampaignsCount, hasCampaignCounts,
  } = campaignsState;
  const [search, setSearch] = useState('');
  const [selectedId, setSelectedId] = useState(null);
  const [saving, setSaving] = useState(false);
  const [submitError, setSubmitError] = useState('');
  const { scrollRef, fadeTop, fadeBottom } = useScrollEdgeFade();

  useEffect(() => {
    if (!open) return;
    setSearch('');
    setSelectedId(null);
    setSubmitError('');
  }, [open]);

  const rows = useMemo(() => metaUnitLinkRows(campaigns, record, search), [campaigns, record, search]);
  const selected = rows.find((r) => r.campaign.id === selectedId && r.status !== 'linked') || null;
  const searchText = search.trim();
  // «Todas (M)» sin las eliminadas (sin número si todavía no se sabe cuántas hay).
  const hasCounts = hasCampaignCounts ?? (activeCampaignsCount != null && totalCampaignsCount != null);
  const showSkeleton = !loaded || (loading && campaigns.length === 0);
  // Carga, vacío, búsqueda sin coincidencias o error: no reservar el alto de la lista.
  const compact = showSkeleton || rows.length === 0;
  const filterOptions = [
    { value: 'active', label: withCount('Activas', hasCounts ? activeCampaignsCount : null) },
    { value: 'all', label: withCount('Todas', hasCounts ? totalCampaignsCount : null) },
  ];
  const filterValue = includeInactive ? 'all' : 'active';

  const handleSubmit = async (e) => {
    e?.preventDefault?.();
    if (saving) return;
    if (!selected) {
      setSubmitError('Elegí una campaña.');
      return;
    }
    if (!record?.itemKind || !record?.id) return;
    setSaving(true);
    setSubmitError('');
    const res = await linkCampaignUnit({
      campaignId: selected.campaign.id,
      records: [{ itemKind: record.itemKind, id: record.id }],
      ownerUserId,
    });
    setSaving(false);
    if (res?._httpOk) {
      onLinked?.(selected.campaign, res);
      return;
    }
    const code = readMetaErrorCode(res);
    setSubmitError(code === 'META_CAMPAIGN_NO_ADS' ? META_LINK_CAMPAIGN_NO_ADS : getMetaErrorMessage(res));
    if (RELOAD_CODES.includes(code)) {
      setSelectedId(null);
      reload({ silent: true });
    }
  };

  const pick = (row) => {
    if (row.status === 'linked' || saving) return;
    setSelectedId(row.campaign.id);
    setSubmitError('');
  };

  let listContent;
  if (showSkeleton) {
    listContent = (
      <Box data-testid="meta-link-campaign-loading" aria-busy="true" sx={{ display: 'flex', flexDirection: 'column' }}>
        {[0, 1, 2].map((i) => <CampaignRowSkeleton key={i} />)}
      </Box>
    );
  } else if (error && campaigns.length === 0) {
    listContent = (
      <Box sx={{ py: 3, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1, textAlign: 'center' }} role="status" data-testid="meta-link-campaign-error">
        <Typography sx={{ fontSize: '14px', fontWeight: 600 }}>No pudimos traer las campañas</Typography>
        <Typography sx={{ fontSize: '13px', color: LEADS_TEXT_SECONDARY }}>{error}</Typography>
        <Button size="small" onClick={() => reload()} sx={{ textTransform: 'none', fontWeight: 600, color: colors.primaryLight }}>Reintentar</Button>
      </Box>
    );
  } else if (rows.length === 0) {
    const bySearch = Boolean(searchText);
    listContent = (
      <Box sx={{ py: 3, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.75, textAlign: 'center' }} role="status" data-testid="meta-link-campaign-empty">
        {bySearch ? null : <CampaignOutlinedIcon sx={{ fontSize: 24, color: '#7F8185' }} aria-hidden />}
        <Typography sx={{ fontSize: '14px', fontWeight: 600 }}>
          {bySearch ? `Ninguna campaña coincide con «${searchText}»` : (includeInactive ? 'No hay campañas' : 'No hay campañas activas')}
        </Typography>
        <Box sx={{ display: 'flex', gap: 2, mt: 0.25 }}>
          {bySearch ? (
            <Button size="small" onClick={() => setSearch('')} sx={{ textTransform: 'none', fontWeight: 600, color: colors.primaryLight }}>Limpiar búsqueda</Button>
          ) : null}
          {!bySearch && !includeInactive ? (
            <Button size="small" onClick={() => setIncludeInactive(true)} sx={{ textTransform: 'none', fontWeight: 600, color: colors.primaryLight }} data-testid="meta-link-campaign-show-all">Ver todas</Button>
          ) : null}
          {!bySearch && onOpenCampaigns ? (
            <Button size="small" onClick={onOpenCampaigns} sx={{ textTransform: 'none', fontWeight: 600, color: colors.primaryLight }}>Ir a Campañas</Button>
          ) : null}
        </Box>
      </Box>
    );
  } else {
    listContent = (
      <Box role="radiogroup" aria-label="Campañas" data-testid="meta-link-campaign-list">
        {rows.map((row) => {
          const { campaign, status, others } = row;
          const disabled = status === 'linked';
          const checked = disabled || selectedId === campaign.id;
          const thumb = metaCampaignThumb(campaign);
          const othersText = describeMetaOtherUnits(others);
          const metrics = metaUnitLinkMetrics(campaign);
          return (
            <Box
              key={campaign.id}
              role="radio"
              aria-checked={checked}
              aria-disabled={disabled || undefined}
              tabIndex={disabled ? -1 : 0}
              onClick={() => pick(row)}
              onKeyDown={(ev) => { if (ev.key === 'Enter' || ev.key === ' ') { ev.preventDefault(); pick(row); } }}
              sx={rowSx(!disabled && selectedId === campaign.id, disabled)}
              data-testid={`meta-link-campaign-${campaign.id}`}
            >
              <Radio checked={checked} disabled={disabled} tabIndex={-1} size="small" sx={radioSx} inputProps={{ 'aria-hidden': true }} />
              <MetaAdThumb url={thumb.url} isVideo={thumb.isVideo} size={40} radius={6} />
              <Box sx={{ minWidth: 0 }}>
                <Typography sx={nameSx(disabled)}>
                  {campaign.name}
                  {status === 'partial' ? <Box component="span" sx={chipSx}>En parte</Box> : null}
                  {othersText ? <Box component="span" sx={chipSx} data-testid={`meta-link-campaign-${campaign.id}-others`}>{othersText}</Box> : null}
                </Typography>
                {disabled ? (
                  <Typography sx={linkedLabelSx} data-testid={`meta-link-campaign-${campaign.id}-linked`}>Ya vinculada</Typography>
                ) : (
                  <Typography sx={lineSx(false)}>{metaUnitLinkDetail(campaign, row)}</Typography>
                )}
                {metrics ? <Typography sx={lineSx(disabled)}>{metrics}</Typography> : null}
              </Box>
            </Box>
          );
        })}
      </Box>
    );
  }

  return (
    <Dialog
      open={open}
      onClose={saving ? undefined : onClose}
      maxWidth={false}
      PaperProps={{
        component: 'form',
        onSubmit: handleSubmit,
        noValidate: true,
        sx: {
          ...appModalPaperSx,
          width: 'min(520px, 95vw)',
          maxWidth: '520px',
          height: compact ? 'auto' : DIALOG_HEIGHT,
          maxHeight: DIALOG_HEIGHT,
        },
      }}
      aria-labelledby="meta-link-campaign-title"
      data-testid="meta-link-campaign-dialog"
      data-layout={compact ? 'compact' : 'list'}
    >
      <DialogTitle id="meta-link-campaign-title" sx={appModalTitleBarSx}>
        <Typography component="h2" sx={appModalTitleSx}>Vincular campaña</Typography>
        <IconButton size="small" onClick={onClose} disabled={saving} aria-label="Cerrar" sx={appModalCloseButtonSx}>
          <CloseIcon sx={{ fontSize: 18 }} />
        </IconButton>
      </DialogTitle>
      <Divider sx={appModalHeaderDividerSx} />
      <DialogContent sx={{ px: '20px', pt: 2, pb: 0, display: 'flex', flexDirection: 'column', gap: 1.25, minHeight: 0, overflow: 'hidden' }}>
        {recordLabel ? <Typography sx={{ fontSize: '14px', fontWeight: 600, overflowWrap: 'anywhere' }}>{recordLabel}</Typography> : null}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <TextField
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Buscar campaña"
            size="small"
            variant="outlined"
            disabled={saving}
            inputProps={{ maxLength: 80, 'aria-label': 'Buscar campaña' }}
            InputProps={{ startAdornment: (<InputAdornment position="start"><SearchIcon sx={listSearchIconSx} /></InputAdornment>) }}
            sx={{ ...listSearchFieldSx, mt: 0 }}
            data-testid="meta-link-campaign-search"
          />
          {/* t999u: desplegable compacto (antes un toggle), mismo alto que el buscador (44 px). */}
          <Select
            size="small"
            value={filterValue}
            onChange={(e) => { setIncludeInactive(e.target.value === 'all'); setSelectedId(null); }}
            disabled={saving}
            inputProps={{ 'aria-label': 'Campañas a mostrar' }}
            MenuProps={leadsSelectMenuProps}
            renderValue={(v) => <FilterValue value={v} options={filterOptions} />}
            sx={{ ...metaLinkFilterSelectSx, flexShrink: 0 }}
            data-testid="meta-link-campaign-filter"
          >
            {filterOptions.map((o) => <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>)}
          </Select>
        </Box>
        {/* Con campañas, la lista toma el alto que sobra y es lo único que scrollea. Sin filas, abraza el aviso. */}
        <Box
          ref={scrollRef}
          sx={{
            flex: compact ? '0 0 auto' : '1 1 0',
            minHeight: compact ? 0 : 120,
            overflowY: compact ? 'visible' : 'auto',
            mx: -0.75,
            px: 0.75,
            ...getScrollEdgeFadeSx(fadeTop, fadeBottom),
          }}
          data-testid="meta-link-campaign-scroll"
        >
          {listContent}
        </Box>
        {submitError ? (
          <Alert severity="error" variant="outlined" sx={leadsAlertSx} data-testid="meta-link-campaign-error-alert">{submitError}</Alert>
        ) : (
          <Typography sx={{ fontSize: '12.5px', color: LEADS_TEXT_SECONDARY }}>{META_LINK_CAMPAIGN_HINT}</Typography>
        )}
      </DialogContent>
      <DialogActions sx={appModalActionsSx}>
        <Button variant="outlined" onClick={onClose} disabled={saving} sx={appModalSecondaryButtonSx}>Cancelar</Button>
        <Button
          type="submit"
          variant="contained"
          disabled={saving || !selected}
          sx={saving ? { ...appModalPrimaryButtonSx, ...appModalPrimaryLoadingSx } : appModalPrimaryButtonSx}
          startIcon={saving ? <CircularProgress size={14} sx={{ color: 'inherit' }} /> : null}
          data-testid="meta-link-campaign-submit"
        >
          Vincular
        </Button>
      </DialogActions>
    </Dialog>
  );
}
